import type { RelatedContext } from './contracts';
import type {
  AIFindResponse,
  SimilarResponse,
  HomeResponse,
  PublicProductDto,
  PublicSkuDto,
  RecommendationResponse,
  SearchResponse,
} from "./contracts";

const apiBase = (import.meta.env.VITE_BEAUTINOW_SEARCH_API_BASE || "").replace(/\/$/, "");

export class ProductSearchApiError extends Error {
  constructor(message: string, readonly code = "request_failed", readonly retryable = false, readonly status = 0, readonly requestId = "") {
    super(message);
  }
}

function endpoint(path: string) {
  if (!apiBase) throw new ProductSearchApiError("商品搜索 API 尚未配置", "api_not_configured", false);
  return `${apiBase}${path}`;
}

export function productSearchImageUrl(source: string | null | undefined, fit = ""): string {
  const value = String(source || "").trim();
  if (!value || !apiBase || value.startsWith("/")) return value;
  try {
    const url = new URL(value);
    const proxy = `${apiBase}/image`;
    if (value.startsWith(proxy)) {
      if (fit) url.searchParams.set("fit", fit);
      return url.toString();
    }
    if (url.protocol !== "https:") return value;
    const params = new URLSearchParams({ src: value });
    if (fit) params.set("fit", fit);
    return `${proxy}?${params}`;
  } catch {
    return value;
  }
}

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = 12_000): Promise<T> {
  const controller = new AbortController();
  const externalSignal = init.signal;
  const abortFromCaller = () => controller.abort();
  externalSignal?.addEventListener("abort", abortFromCaller, { once: true });
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(endpoint(path), { ...init, credentials: "include", signal: controller.signal });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = payload?.error ?? (typeof payload?.detail === "string" ? payload.detail : undefined);
      const detail = typeof error === "object" && error ? error : payload;
      const requestId = payload?.requestId || response.headers.get("X-Request-ID") || "";
      throw new ProductSearchApiError(
        typeof error === "string" ? error : detail?.message || "商品服务暂时不可用",
        detail?.code || "request_failed",
        Boolean(detail?.retryable),
        response.status,
        requestId,
      );
    }
    return payload as T;
  } catch (error) {
    if (error instanceof ProductSearchApiError) throw error;
    if (error instanceof DOMException && error.name === "AbortError") {
      if (externalSignal?.aborted) throw new ProductSearchApiError("请求已取消", "aborted", false);
      throw new ProductSearchApiError("商品服务响应超时，请稍后重试", "request_timeout", true);
    }
    throw new ProductSearchApiError("网络连接失败，请检查网络后重试", "network_error", true);
  } finally {
    window.clearTimeout(timer);
    externalSignal?.removeEventListener("abort", abortFromCaller);
  }
}

function query(params: Record<string, string | number | boolean | undefined>) {
  const values = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") values.set(key, String(value));
  });
  return values.toString();
}

export const productSearchApi = {
  wishlistRequest<T>(path: string, init: RequestInit = {}, timeoutMs = 12_000) {
    return request<T>(path, init, timeoutMs);
  },
  skuAvailability(productKey:string,skuKey:string) {
    return request<{productKey:string;sku:PublicSkuDto}>(`/products/${encodeURIComponent(productKey)}/skus/${encodeURIComponent(skuKey)}/availability`);
  },
  aiFind(payload: { text: string; imageDataUrl?: string }, signal?: AbortSignal) {
    return request<AIFindResponse>("/ai-find", {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload),signal}, 60_000);
  },
  related(productKey: string, context: RelatedContext = {}) {
    const params = new URLSearchParams();
    if(context.contextBrand)params.set('contextBrand',context.contextBrand);
    if(context.contextQuery)params.set('contextQuery',context.contextQuery);
    if(context.sceneToken)params.set('sceneToken',context.sceneToken);
    for(const key of context.exclude || [])params.append('exclude',key);
    return request<{referenceKey: string; products: PublicProductDto[]; poolToken?: string}>(`/products/${encodeURIComponent(productKey)}/related?${params}`);
  },
  similar(productKey: string, filters: {brands: string[]; scents: string[]; priceRanges: string[]}, offset = 0, context: RelatedContext = {}, poolToken = '') {
    const params = new URLSearchParams({offset:String(offset),limit:'20'});
    if(context.contextBrand)params.set('contextBrand',context.contextBrand);
    if(context.contextQuery)params.set('contextQuery',context.contextQuery);
    if(context.sceneToken)params.set('sceneToken',context.sceneToken);
    if(poolToken)params.set('poolToken',poolToken);
    for(const key of context.exclude || [])params.append('exclude',key);
    for (const [key, values] of Object.entries(filters)) for (const value of values) params.append(key,value);
    return request<SimilarResponse>(`/products/${encodeURIComponent(productKey)}/similar?${params}`);
  },
  home(limit = 12) {
    return request<HomeResponse>(`/home?${query({ limit })}`);
  },
  search(params: { q: string; limit?: number; offset?: number; confirmBrand?: boolean; literal?: boolean }) {
    return request<SearchResponse>(`/search?${query(params)}`);
  },
  brands() {
    return request<{ catalogVersion: number; brands: SearchResponse["brands"] }>("/brands");
  },
  product(productKey: string) {
    return request<PublicProductDto>(`/products/${encodeURIComponent(productKey)}`);
  },
  recommendations(payload: unknown) {
    return request<RecommendationResponse>("/recommendations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  },
};
